import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { type UseMutationResult, useMutation } from '@tanstack/react-query';
import { StorageKey } from '../../hooks/model/StorageKey';
import { useLocalStoragePubSub } from '../../hooks/useLocalStoragePubSub';
import { useCustomPreferenceOrDefault } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { setPreviewDateInUrl } from '../../utils/preview-mode-utils';
import { bffPut } from '../bff';
import { type CustomerContext } from './model/CustomerContext';

export const useCustomerUpdateContext = (): UseMutationResult<
    CustomerContext,
    unknown,
    CustomerContext
> => {
    const pwaSurlatablePreview = useCustomPreferenceOrDefault(
        CustomPreference.PWA_SURLATABLE_PREVIEW,
        false,
    );
    const publishPreviewDate = useLocalStoragePubSub(StorageKey.PREVIEW_MODE_DATE);
    const authContext = useAuthContext();
    return useMutation<CustomerContext, unknown, CustomerContext>({
        mutationFn: async (body: CustomerContext) => {
            if (!pwaSurlatablePreview) {
                throw new Error('PWA Surlatable Preview is not enabled');
            }
            const authData = await authContext.ready();
            if (!authData.usid) {
                throw new Error('User ID is required');
            }
            return bffPut<CustomerContext>(`/contexts/${authData.usid}`, {
                body,
                httpParams: [],
                accessToken: authData.access_token,
                customerId: authData.customer_id,
            });
        },
        onSuccess: (context: CustomerContext) => {
            if (context) {
                try {
                    publishPreviewDate(context.effectiveDateTime as string);
                } catch (error) {
                    // Log but don't fail the mutation
                    console.warn('Failed to publish preview date:', error);
                }
                setPreviewDateInUrl(context.effectiveDateTime as string);
            }
        },
    });
};
